import { useMemo } from 'react';

import { Icon } from '@rbilabs/universal-components';

import { ProductDetailListItem } from 'components/product-detail-list-item';
import { ProductDetailListItemGroup } from 'components/product-detail-list-item-group';
import { ProductProps } from 'components/product-detail-list-item/product/types';
import { IProductDetailListItemProps } from 'components/product-detail-list-item/types';
import { EntryTypeEnum, IPriceDto } from 'generated/graphql-gateway';
import { FormatCaloriesFn, useMenuCalories } from 'hooks/menu/use-menu-calories';
import { useUnifiedMenuContext } from 'state/unified-menu';
import { ILocalizedMenuEntry, IndexedMenu, PosServiceMode } from 'state/unified-menu/types';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import {
  getComboSlotItemPrice,
  getFirstDefaultId,
  hasOptionEntries,
  isCombo,
  isItem,
  mapImageResourceToSanityImage,
} from 'state/unified-menu/utils';
import { isDummyItem } from 'utils/minimal-cart';

import { useProductWizardContext } from '../../product-wizard';
import {
  DEFAULT_SLOT_KEY,
  ModifiersSelections,
  SlotSelections,
  SlotsSelections,
} from '../../product-wizard/types';
import { useProductWizardFlow } from '../../product-wizard/use-product-wizard-flow';
import { computeMenuItemPrice } from '../../product-wizard/utils';
import { mapModifier } from '../modifier/utils';
import { isCustomizableEntry } from '../product-customization/utils';

import { ProductComboFlowContainer, ProductComboFlowInnerLayout } from './styled';

export const ProductCombo: React.FC = () => {
  const { formatCalories } = useMenuCalories();
  const { indexedMenu } = useUnifiedMenuContext();
  const { posServiceMode } = useMenuQueryOptions();
  const { selectedProduct, slotsSelections } = useProductWizardContext();
  const { goToCustomization } = useProductWizardFlow();

  const mappedItems = useMemo(
    () =>
      createProductDetailListItemProps(
        indexedMenu,
        posServiceMode,
        selectedProduct,
        slotsSelections,
        formatCalories,
        goToCustomization
      ),
    [
      indexedMenu,
      posServiceMode,
      selectedProduct,
      slotsSelections,
      formatCalories,
      goToCustomization,
    ]
  );

  return (
    <ProductComboFlowContainer testID="product-combo-flow" key={1}>
      <ProductComboFlowInnerLayout>
        <ProductDetailListItemGroup
          items={mappedItems.map(({ key, ...others }) => (
            <ProductDetailListItem {...others} key={key} />
          ))}
        />
      </ProductComboFlowInnerLayout>
    </ProductComboFlowContainer>
  );
};

interface IProductDetailListItemPropsWithKey extends IProductDetailListItemProps {
  key: string;
}

interface IRow {
  item: ILocalizedMenuEntry;
  contextualPrice?: IPriceDto;
  defaultContextPrice?: IPriceDto;
  slotSelection?: SlotSelections;
  slotKey: string;
  isCustomizable: boolean;
}

function createProductDetailListItemProps(
  indexedMenu: IndexedMenu,
  posServiceMode: PosServiceMode,
  selectedProduct: ILocalizedMenuEntry,
  slotsSelections: SlotsSelections,
  formatCalories: FormatCaloriesFn,
  goToCustomization: ReturnType<typeof useProductWizardFlow>['goToCustomization']
): IProductDetailListItemPropsWithKey[] {
  const result: IProductDetailListItemPropsWithKey[] = [];

  const rows: IRow[] = [];

  if (isCombo(selectedProduct)) {
    for (const comboSlotRef of selectedProduct?.options?.entries ?? []) {
      const comboSlot = indexedMenu.get(comboSlotRef.entryId);
      const slotSelection = slotsSelections[comboSlot.id];

      let contextualPrice;
      let defaultContextPrice;
      let item: ILocalizedMenuEntry;
      if (slotSelection?.itemId) {
        item = indexedMenu.get(slotSelection.itemId, { type: EntryTypeEnum.ITEM });
        contextualPrice = getComboSlotItemPrice(indexedMenu, comboSlot.id, slotSelection.itemId);
        defaultContextPrice = getComboSlotItemPrice(indexedMenu, comboSlot.id, undefined);
      } else {
        const defaultItemId = getFirstDefaultId(comboSlot);
        item = indexedMenu.get(defaultItemId);
        const defaultPrice = getComboSlotItemPrice(indexedMenu, comboSlot.id, undefined);
        contextualPrice = defaultPrice;
        defaultContextPrice = defaultPrice;
      }

      rows.push({
        item,
        contextualPrice,
        defaultContextPrice,
        slotKey: comboSlot.id,
        slotSelection,
        isCustomizable: isCustomizableEntry(comboSlot, indexedMenu, posServiceMode),
      });
    }
  } else if (isItem(selectedProduct)) {
    rows.push({
      item: selectedProduct,
      contextualPrice: selectedProduct.price ?? undefined,
      defaultContextPrice: selectedProduct.price ?? undefined,
      slotKey: DEFAULT_SLOT_KEY,
      slotSelection: slotsSelections[DEFAULT_SLOT_KEY],
      isCustomizable: isCustomizableEntry(selectedProduct, indexedMenu, posServiceMode),
    });
  }

  if (!rows.length) {
    return result;
  }

  for (const row of rows) {
    const {
      item,
      contextualPrice,
      defaultContextPrice,
      slotSelection,
      slotKey,
      isCustomizable,
    } = row;

    const productName = item.name?.locale ?? item.id;

    if (isDummyItem(productName)) {
      continue;
    }

    const modifierSelections = slotSelection?.modifiers ?? {};
    const itemPrice = computeMenuItemPrice(
      indexedMenu,
      item,
      posServiceMode,
      contextualPrice,
      modifierSelections
    );
    const defaultPrice = defaultContextPrice?.[posServiceMode] ?? 0;

    // Only show price for upsell combo slots or customizations
    const price = itemPrice === defaultPrice ? undefined : Math.abs(itemPrice - defaultPrice);
    const calories = item.nutrition?.calories?.def ?? 0;
    const selections = getProductSelections(indexedMenu, posServiceMode, item, modifierSelections);

    const imgSrc = item.image?.resource;
    const image = imgSrc ? mapImageResourceToSanityImage(imgSrc) : undefined;
    result.push({
      key: slotKey,
      isClickable: isCustomizable,
      productProps: {
        name: productName,
        calories: formatCalories(calories),
        image: image ? JSON.stringify(image) : undefined,
        isProductNameBold: true,
        price,
        selections,
      },
      controls: isCustomizable ? <Icon variant="next" testID="next-icon" /> : undefined,
      onNavigate: () => goToCustomization(slotKey),
    });
  }

  return result;
}

function getProductSelections(
  menu: IndexedMenu,
  posServiceMode: PosServiceMode,
  entry: ILocalizedMenuEntry,
  modifiersSelections: ModifiersSelections
): ProductProps['selections'] {
  const result: ProductProps['selections'] = [];
  if (!hasOptionEntries(menu, posServiceMode, entry)) {
    return result;
  }

  for (const [modifierId, multipliers] of Object.entries(modifiersSelections)) {
    const modifier = mapModifier(menu, modifierId, entry.options.entries.length, posServiceMode);
    for (const [multiplierId, quantity] of Object.entries(multipliers)) {
      const multiplier = modifier.multipliers.find(({ id }) => id === multiplierId);
      if (!multiplier) {
        // this should never happen
        continue;
      }

      result.push({
        id: `${modifierId}-${multiplierId}`,
        name: `${multiplier.prefix} ${modifier.name}`,
        calories: multiplier.nutrition?.calories?.def ?? 0,
        multiplier: multiplier.multiplier,
        price: (multiplier.price ?? 0) * quantity,
      });
    }
  }

  return result;
}
